/**
 * Takes two objects - one of values and one of names - and creates a useful
 * resulting array of objects for use in iterating. Mostly used for creating form inputs.
 *
 * @param {Object} list The object whose values are the input value
 * @param {Object} names An object keyed by the input value; Values meant for display
 * @return {Array}
 */
export const createOptions = (list, names) =>
  Object.values(list).map((value) => ({
    display: names[value],
    value,
  }))

// /////////////
// Onboarding //
// /////////////

export const OnboardingSteps = {
  // These numbers do not control ordering, they are essentially ids
  login: 0,
  registration: 1,
  eventType: 2,
  eventDate: 3,
  personalInfo: 4,
  weddingPartyCount: 5,
  eventSummary: 6,
  attributionSurvey: 7,
  groomsRentFree: 8,
}

export const OnboardingStepNames = {
  // These numbers should correspond to OnboardingSteps
  0: 'Login',
  1: 'Signup',
  2: 'Event Type',
  3: 'Event Date',
  4: 'Customer Data',
  5: 'Wedding Party Count',
  6: 'Summary',
  7: 'Attribution',
  8: 'Grooms Rent Free',
}

export const SpecialEventFunnel = [
  OnboardingSteps.eventType,
  OnboardingSteps.eventDate,
  OnboardingSteps.registration,
  OnboardingSteps.attributionSurvey,
]

export const GuestOrGroomsmanFunnel = [
  OnboardingSteps.eventType,
  OnboardingSteps.personalInfo,
  OnboardingSteps.eventDate,
  OnboardingSteps.registration,
  OnboardingSteps.attributionSurvey,
]

export const OwnerFunnel = [
  OnboardingSteps.eventType,
  OnboardingSteps.personalInfo,
  OnboardingSteps.eventDate,
  OnboardingSteps.weddingPartyCount,
  OnboardingSteps.registration,
  OnboardingSteps.attributionSurvey,
  OnboardingSteps.groomsRentFree,
]

export const RegisteredSpecialEventFunnel = [
  OnboardingSteps.eventType,
  OnboardingSteps.eventDate,
  OnboardingSteps.eventSummary,
]

export const RegisteredGuestOrGroomsmanFunnel = [
  OnboardingSteps.eventType,
  OnboardingSteps.personalInfo,
  OnboardingSteps.eventDate,
  OnboardingSteps.eventSummary,
]

export const RegisteredOwnerFunnel = [
  OnboardingSteps.eventType,
  OnboardingSteps.personalInfo,
  OnboardingSteps.eventDate,
  OnboardingSteps.weddingPartyCount,
  OnboardingSteps.eventSummary,
  OnboardingSteps.groomsRentFree,
]

// Event Types

export const EventTypes = {
  wedding: 0,
  prom: 1,
  specialEvent: 3,
}

export const EventTypeNames = {
  [EventTypes.wedding]: 'Wedding',
  [EventTypes.prom]: 'Prom',
  [EventTypes.specialEvent]: 'Special Event',
}

// [({display, value})]
export const EventTypeOptions = createOptions(EventTypes, EventTypeNames)

// Event Roles

export const EventRoles = {
  bride: 'Bride',
  groom: 'Groom',
  groomsman: 'Groomsman',
  weddingGuest: 'Guest',
}

export const EventRoleNames = {
  [EventRoles.bride]: 'Bride',
  [EventRoles.groom]: 'Groom',
  [EventRoles.groomsman]: 'Groomsman',
  [EventRoles.weddingGuest]: 'Guest',
}

// [({display, value})]
export const EventRoleOptions = createOptions(EventRoles, EventRoleNames)

// Event Num Members

export const EventNumMembers = [1, 2, 3, 4, 5, 6, 7, 8, 9]

export const EventNumMemberNames = {
  1: '1',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9+',
}

// [({display, value})]
export const EventNumMemberOptions = createOptions(EventNumMembers, EventNumMemberNames)

// Event Detail Types

export const EventDetailTypes = {
  date: 'Date',
  name: 'Name',
  type: 'Type',
}

// if adding to this, check with Claus about naming preference
export const SurveyNames = {
  onboardingAttribution: 'onboarding attribution',
}

// if adding to this, check with Claus about naming preference
export const SurveyQuestions = {
  attribution: 'how did you hear about us',
}

// //////////
// Madlibs //
// //////////

export const Madlibs = {
  eventDate: 'event-date',
  eventType: 'event-type',
  eventRole: 'event-role',
  numPeople: 'num-people',
}

export const MadlibsContext = {
  dropdown: 'dropdown',
  modal: 'modal',
}

// ////////////
// Scheduler //
// ////////////

export const SchedulerRoles = {
  bride: 'Bride',
  fatherBride: 'Father Bride',
  fatherGroom: 'Father Groom',
  groom: 'Groom',
  groomsman: 'Groomsman',
  officiant: 'Officiant',
  other: 'Other',
  usher: 'Usher',
  weddingGuest: 'Wedding Guest',
  weddingParty: 'Wedding Party',
}

// the names are the same as the identifiers, for now, but they don't have to
//  be
export const SchedulerRoleNames = {
  [SchedulerRoles.bride]: 'Bride',
  [SchedulerRoles.fatherBride]: 'Father Bride',
  [SchedulerRoles.fatherGroom]: 'Father Groom',
  [SchedulerRoles.groom]: 'Groom',
  [SchedulerRoles.groomsman]: 'Groomsman',
  [SchedulerRoles.officiant]: 'Officiant',
  [SchedulerRoles.other]: 'Other',
  [SchedulerRoles.usher]: 'Usher',
  [SchedulerRoles.weddingGuest]: 'Wedding Guest',
  [SchedulerRoles.weddingParty]: 'Wedding Party',
}

// [({display, value})]
export const SchedulerRoleOptions = createOptions(SchedulerRoles, SchedulerRoleNames)

export const SchedulerRoleEventRoles = {
  [SchedulerRoles.bride]: EventRoles.bride,
  [SchedulerRoles.fatherBride]: EventRoles.weddingGuest,
  [SchedulerRoles.fatherGroom]: EventRoles.weddingGuest,
  [SchedulerRoles.groom]: EventRoles.groom,
  [SchedulerRoles.groomsman]: EventRoles.weddingGuest,
  [SchedulerRoles.officiant]: EventRoles.weddingGuest,
  [SchedulerRoles.other]: EventRoles.weddingGuest,
  [SchedulerRoles.usher]: EventRoles.weddingGuest,
  [SchedulerRoles.weddingGuest]: EventRoles.weddingGuest,
  [SchedulerRoles.weddingParty]: EventRoles.weddingGuest,
}

// //////////////
// Checkout    //
// //////////////

export const CheckoutStepSlugs = {
  shipping: 'shipping-step',
  partner: 'partner-step',
  billing: 'billing-step',
  review: 'review-step',
}

export const CheckoutStepLabels = {
  [CheckoutStepSlugs.shipping]: 'Shipping',
  [CheckoutStepSlugs.partner]: 'Partner',
  [CheckoutStepSlugs.billing]: 'Billing',
  [CheckoutStepSlugs.review]: 'Review',
}

export const CheckoutFunnels = {
  coreg: [
    CheckoutStepSlugs.shipping,
    CheckoutStepSlugs.partner,
    CheckoutStepSlugs.billing,
    CheckoutStepSlugs.review,
  ],
  default: [CheckoutStepSlugs.shipping, CheckoutStepSlugs.billing, CheckoutStepSlugs.review],
}

// //////////////////
// Look Name Panel //
// //////////////////

export const WeddingEventLookNames = {
  groom: 'Groom',
  groomsman: 'Groomsman',
  weddingParty: 'Wedding Party',
  ushers: 'Ushers',
  fatherBride: 'Father - Bride',
  fatherGroom: 'Father - Groom',
  weddingGuest: 'Wedding Guest',
  officiant: 'Officiant',
}

export const OtherEventLookNames = {
  prom: 'Prom',
  specialEvent: 'Special Event',
}

// //////////////////
// Co-Registration //
// //////////////////

export const PartnerInfoAnalyticsOrigin = {
  APPOINTMENT: 'Appointment Confirmation Page',
  CHECKOUT: 'Checkout Post Shipping',
}

// ////////////
// Showrooms //
// ////////////

/**
 * Enum for showroom types
 * @readonly
 * @enum {string}
 */
export const ShowroomTypes = Object.freeze({
  theBlackTux: 'theblacktux',
  davidsBridal: 'davids-bridal',
  nordstrom: 'nordstrom',
})
